import React from 'react'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import { H1, H4, P } from '@jarvis.oddle/core/components/Typo'
import styled from 'styled-components'
import {
  colorConstants,
  margin,
  marginSizes,
  padding,
  paddingSizes
} from '@jarvis.oddle/core/theme/default'
import Divider from '@jarvis.oddle/core/components/Divider'
import { formatDate } from '@jarvis.oddle/core/tools/date'
import Form from '@jarvis.oddle/core/components/Form'
import Div from '@jarvis.oddle/core/components/Div'
import Modal, {
  CloseButton,
  PrevButton
} from '@jarvis.oddle/core/components/Modal'
import IconOddleEats from '@jarvis.oddle/core/components/Icons/IconOddleEats'
import showNotificationToast from '@jarvis.oddle/core/components/ToastNotification'
import { isObjectEmpty } from 'src/utils/object'
import { getVoucherReward } from 'tools/voucher'
import { validateVoucherAction } from 'src/redux/checkout'
import useAppDispatch from 'src/hooks/useAppDispatcher'
import modalController, {
  makeControlledModal
} from 'src/decorators/ModalController'
import { useAppContext } from 'src/containers/AppContext'

const StyledVoucherDescription = styled(P)`
  &&& {
    font-family: Roboto, serif;
    color: ${colorConstants.defaultText};
    margin-top: ${margin[marginSizes.small]};
  }
`

const StyledModalHeader = styled(Modal.Header)`
  box-shadow: none;

  &&& {
    background-color: ${colorConstants.white};
  }
`

const StyledModalSection = styled(Modal.Section)`
  margin-top: ${margin[marginSizes.none]};
  height: 100%;
  padding: ${padding[paddingSizes.medium]};

  &&& {
    background-color: ${colorConstants.white};
  }
`

const StyledModalFooter = styled(Modal.Footer)`
  &&& {
    background-color: ${colorConstants.white};
  }
`

const SubmitButton = ({
  voucher
}: {
  voucher: {
    id?: string
    code?: string
    discount?: Record<string, unknown>
    isValid?: boolean
  }
}) => {
  const { t } = React.useContext(TranslationContext)
  return (
    <Form.SubmitButton
      buttonColor={colorConstants.mainVoucherColor}
      buttonTextColor={colorConstants.white}
      disabled={!voucher.isValid}
      fullWidth
      buttonStyle='solid'
      buttonShape='round'
    >
      {({ loading, LoadingElement }) =>
        loading ? (
          LoadingElement
        ) : (
          <H4 normalised bold fontFamily='Roboto' color={colorConstants.white}>
            {t('Apply Voucher')}
          </H4>
        )
      }
    </Form.SubmitButton>
  )
}

const WrapperVoucherDetailsModal = ({
  voucher = {},
  modal,
  title,
  description,
  startDate,
  expirationDate,
  code,
  metadata = {}
}: {
  voucher: {
    id?: string
    code?: string
    discount?: Record<string, unknown>
    isValid?: boolean
  }
  modal: {
    show: boolean
    onClose: (params) => void
    onClosed: () => void
  }
  title: string
  description: string
  startDate: string
  expirationDate: string
  code: string
  metadata: Record<string, unknown>
}) => {
  const dispatch = useAppDispatch()
  const { t } = React.useContext(TranslationContext)
  const { formatMoney } = useAppContext()

  const handleClickBackButton = () => {
    modal.onClose(() => {
      modalController.showModal('VouchersSelectorModal')
    })
  }

  const handleSubmit = React.useCallback(
    async (values, { setSubmitting }) => {
      setSubmitting(true)
      const { code, id } = voucher || {}
      if (values.selectedVoucherID === id) {
        const { payload: { error } } = await dispatch(
          validateVoucherAction({ code })
        )
        modalController.closeModal('VoucherDetailsModal')
        if (isObjectEmpty(error)) {
          showNotificationToast.success(t('Voucher Applied Successfully!'))
        } else {
          showNotificationToast.error(
            t('Voucher cannot be applied at this time')
          )
        }
      }
      setSubmitting(false)
    },
    [dispatch, t, voucher]
  )

  return (
    <Modal {...modal} slideInAnimation='bottom'>
      <Form
        initialValues={{ selectedVoucherID: voucher.id }}
        onSubmit={handleSubmit}
      >
        <Modal.FlexContainer flexDirection='column'>
          <StyledModalHeader>
            <PrevButton
              onClick={handleClickBackButton}
              fill={colorConstants.black}
              iconSize='large'
            />
            <IconOddleEats width='120px' height='100%' />
            <CloseButton onClick={modal.onClose} />
          </StyledModalHeader>
          <Modal.Body>
            <StyledModalSection>
              <React.Fragment>
                <H1 bold fontFamily='Roboto' color={colorConstants.black}>
                  {title}
                </H1>
                {description && (
                  <StyledVoucherDescription normalised>
                    {description}
                  </StyledVoucherDescription>
                )}
                <Div
                  marginTop={marginSizes.medium}
                  marginBottom={marginSizes.medium}
                >
                  <Divider color={colorConstants.lightGray} />
                </Div>
                <Div marginBottom={marginSizes.medium}>
                  <H4
                    bold
                    normalised
                    color={colorConstants.black}
                    fontFamily='Roboto'
                  >
                    {t('Code:')}
                  </H4>
                  <P
                    normalised
                    color={colorConstants.black}
                    fontFamily='Roboto'
                  >
                    {code}
                  </P>
                </Div>
                {!isObjectEmpty(voucher.discount) && (
                  <Div marginBottom={marginSizes.medium}>
                    <H4
                      bold
                      normalised
                      color={colorConstants.black}
                      fontFamily='Roboto'
                    >
                      {t('Voucher Reward:')}
                    </H4>
                    <P
                      normalised
                      color={colorConstants.black}
                      fontFamily='Roboto'
                    >
                      {t('_rewardAmount_ off', {
                        rewardAmount: getVoucherReward(voucher, formatMoney)
                      })}
                    </P>
                  </Div>
                )}
                {startDate && expirationDate && (
                  <Div marginBottom={marginSizes.medium}>
                    <H4
                      bold
                      normalised
                      color={colorConstants.black}
                      fontFamily='Roboto'
                    >
                      {t('Validity Period:')}
                    </H4>
                    <P
                      normalised
                      color={colorConstants.black}
                      fontFamily='Roboto'
                    >
                      {t('_startDate_ - _endDate_', {
                        startDate: formatDate({
                          date: startDate,
                          options: {
                            weekday: undefined,
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          }
                        }),
                        endDate: formatDate({
                          date: expirationDate,
                          options: {
                            weekday: undefined,
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          }
                        })
                      })}
                    </P>
                  </Div>
                )}
                {!startDate && expirationDate && (
                  <Div marginBottom={marginSizes.medium}>
                    <H4
                      bold
                      normalised
                      color={colorConstants.black}
                      fontFamily='Roboto'
                    >
                      {t('Validity Period:')}
                    </H4>
                    <P
                      normalised
                      color={colorConstants.black}
                      fontFamily='Roboto'
                    >
                      {t('Until _endDate_', {
                        endDate: formatDate({
                          date: expirationDate,
                          options: {
                            weekday: undefined,
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          }
                        })
                      })}
                    </P>
                  </Div>
                )}
                {startDate && !expirationDate && (
                  <Div marginBottom={marginSizes.medium}>
                    <H4
                      bold
                      normalised
                      color={colorConstants.black}
                      fontFamily='Roboto'
                    >
                      {t('Validity Period:')}
                    </H4>
                    <P
                      normalised
                      color={colorConstants.black}
                      fontFamily='Roboto'
                    >
                      {t('Valid from _startDate_', {
                        startDate: formatDate({
                          date: startDate,
                          options: {
                            weekday: undefined,
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          }
                        })
                      })}
                    </P>
                  </Div>
                )}
                {metadata.minTotalOrderAmount && (
                  <Div marginBottom={marginSizes.medium}>
                    <H4
                      bold
                      normalised
                      color={colorConstants.black}
                      fontFamily='Roboto'
                    >
                      {t('Minimum Total Order Amount:')}
                    </H4>
                    <P
                      normalised
                      color={colorConstants.black}
                      fontFamily='Roboto'
                    >
                      {metadata.currencyCode}
                      {formatMoney(metadata.minTotalOrderAmount as number)}
                    </P>
                  </Div>
                )}
                {metadata.paymentMethod && (
                  <Div marginBottom={marginSizes.medium}>
                    <H4
                      bold
                      normalised
                      color={colorConstants.black}
                      fontFamily='Roboto'
                    >
                      {t('Payment Method:')}
                    </H4>
                    <P
                      normalised
                      color={colorConstants.black}
                      fontFamily='Roboto'
                    >
                      {t(metadata.paymentMethod)}
                    </P>
                  </Div>
                )}
                {metadata.menuEligibility && (
                  <Div marginBottom={marginSizes.medium}>
                    <H4
                      bold
                      normalised
                      color={colorConstants.black}
                      fontFamily='Roboto'
                    >
                      {t('Menu Eligibility:')}
                    </H4>
                    <P
                      normalised
                      color={colorConstants.black}
                      fontFamily='Roboto'
                    >
                      {t(metadata.menuEligibility)}
                    </P>
                  </Div>
                )}
                {metadata.otherInformation && (
                  <Div marginBottom={marginSizes.medium}>
                    <H4
                      bold
                      normalised
                      color={colorConstants.black}
                      fontFamily='Roboto'
                    >
                      {t('Other Information:')}
                    </H4>
                    <P
                      normalised
                      color={colorConstants.black}
                      fontFamily='Roboto'
                    >
                      {t(metadata.otherInformation)}
                    </P>
                  </Div>
                )}
              </React.Fragment>
            </StyledModalSection>
          </Modal.Body>
          <StyledModalFooter>
            <SubmitButton voucher={voucher} />
          </StyledModalFooter>
        </Modal.FlexContainer>
      </Form>
    </Modal>
  )
}

export default makeControlledModal(
  WrapperVoucherDetailsModal,
  'VoucherDetailsModal'
)
